<template>
    <div class="layout-body" :class="{ 'hide-tabs': hideTabs }">
        <Tabs v-if="!hideTabs" />
        <div class="layout-main">
            <div class="layout-main-container">
                <div
                    v-loading="pageLoading"
                    class="route-main h-full"
                    :element-loading-spinner="defaultLoadingSvg"
                    element-loading-svg-view-box="0 0 64 64"
                >
                    <Suspense>
                        <RouterView v-slot="{ Component }">
                            <!-- 手动管理缓存列表 -->
                            <KeepAlive v-for="item in cacheFormArr" :key="item">
                                <component
                                    :is="Component"
                                    v-if="PAGE_TYPE.CACHE_FORM.code === route.meta.pageType && item === route.fullPath"
                                    :key="route.fullPath"
                                />
                            </KeepAlive>
                            <!-- 缓存常规页面 -->
                            <KeepAlive>
                                <component
                                    :is="Component"
                                    v-if="isRouterShow && isKeepAlive && PAGE_TYPE.CACHE_FORM.code !== route.meta.pageType"
                                    :key="route.fullPath"
                                />
                            </KeepAlive>
                            <component :is="Component" v-if="isRouterShow && !isKeepAlive" :key="route.path" />
                        </RouterView>
                    </Suspense>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, provide, ref, watch } from "vue";
import { useRoute } from "vue-router";
import Tabs from "../tabs/index.vue";
import useTableHeight from "./useTableHeight";
import { PAGE_TYPE } from "@supplier/meta";
import { useGlobalStore } from "@/stores/global";
import { useKeepAliveStore } from "@/stores/keepAlive";
import { defaultLoadingSvg } from "@/views/layouts/main/loading";

// 动态计算 列表页面的表格的高度，防止出现2个滚动条
useTableHeight();
const route = useRoute();
const keepAliveStore = useKeepAliveStore();
const cacheFormArr = computed(() => keepAliveStore.cacheFormArr);

// 注入刷新页面方法
const isRouterShow = ref(true);

// 页面首次接口请求展示loading（详情页、编辑页）
const globalStore = useGlobalStore();
const pageLoading = computed(() => globalStore.pageLoading && route?.meta?.pageType !== PAGE_TYPE.LIST.code);

const refreshCurrentPage = (val: boolean) => {
    keepAliveStore.deleteCacheForm(route.name as string);
    // 删掉后重新存
    if (val && route.meta.pageType === PAGE_TYPE.CACHE_FORM.code) {
        keepAliveStore.addCacheForm(route.name as string, route.fullPath as string);
    }
    isRouterShow.value = val;
};
provide("refresh", refreshCurrentPage);

/**
 * 特别指定的缓存页, 虽然类型不是列表页, 但是仍然需要缓存
 */
const specifyKeepAliveIncludePages: string[] = [];
/**
 * 特别指定取消缓存的页面, 虽然类型是列表页, 但是不需要缓存
 */
const specifyKeepAliveExcludePages: string[] = [];
const isKeepAlive = computed(() => {
    if (specifyKeepAliveExcludePages.includes(route.name as string)) return false;

    if ([PAGE_TYPE.LIST.code, PAGE_TYPE.LOG.code, PAGE_TYPE.CACHE_FORM.code].includes(route.meta.pageType as number)) {
        return true;
    }

    return specifyKeepAliveIncludePages.includes(route.name as string);
});

watch(
    () => route.fullPath,
    () => {
        // 缓存配置的表单页
        if (route.meta.pageType === PAGE_TYPE.CACHE_FORM.code) {
            keepAliveStore.addCacheForm(route.name as string, route.fullPath as string);
        }
    },
    { immediate: true }
);

// 判断是否显示 tabs
const hideTabs = computed(() => {
    // 如果 hideTabs 为 true时不显示 tabs
    return route.meta.hideTabs;
});
</script>

<style lang="scss" scoped>
.layout-content.hide-menu {
    .layout-body {
        max-width: 100%;
    }
}

// 菜单折叠时，主内容区域宽度需要减去 56px（折叠后的菜单宽度）
.layout-content.aside-expand {
    .layout-body {
        max-width: 100%;
    }
}

.layout-body {
    box-sizing: border-box;
    flex: 1 1 auto;
    min-width: 0;
    max-width: 100%;
    padding: 0;
    &.hide-tabs {
        .layout-main,
        .layout-main-container {
            height: 100%;
        }
    }
    .layout-main {
        width: 100%;
        min-width: 0;
        height: var(--zh-layout-main-height);
    }
    .layout-main-container {
        position: relative;
        box-sizing: border-box;
        display: flex;
        flex: 1;
        flex-direction: column;
        height: var(--zh-layout-main-height);
        overflow-y: auto;
        &:has(.white-bg) {
            background: #ffffff;
        }
        border-radius: 8px 8px 0 0;
        overflow-x: auto;

        .route-main {
            position: relative;
            height: 100%;
            min-width: 0;
            &:has(.operation-fixed-btn),
            &:has(.btn-box-fixed) {
                &:after {
                    content: " ";
                    display: block;
                    padding-bottom: 86px;
                }
            }
        }
    }
    .layout-main-container:has(.list-page) {
        .route-main {
            // 列表页保留系统最小内容宽度，横向滚动限制在右侧内容区，避免左侧菜单被整体滚动带走。
            min-width: calc(var(--zh-layout-min-width) - var(--el-aside-width) - 16px);
        }
    }
    .customize {
        padding: 0 !important;
        background: transparent !important;
    }
}
</style>
